// /support/:conversationNumber \u2014 conversation-detail route (extended for
// issue #9 thread renderer; original gating contract from issue #11 / ADR-024
// is preserved).
//
// On mount, calls the `get-conversation` Edge Function with the user's
// Supabase JWT. The Edge Function is the trust boundary; this route is the
// presentation surface:
//
//   - On 200, renders the conversation header (subject, status, customer
//     name, timestamps) AND the thread bubbles below.
//   - On 403, renders the shared `AuthErrorLayout` with the
//     spec-mandated friendly message. The body is identical for the
//     three 403 sub-cases (tag_blocked / not_authorized / not_found) per
//     issue #11 invariant 2.
//   - On 401, redirects to `/login` (auth expired between mount and now).
//   - On other errors, renders the same `AuthErrorLayout` with a generic
//     fallback message and a retry CTA.

import * as React from 'react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ChevronLeft, Reply } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { env } from '../lib/env';
import { logger } from '../lib/logger';
import { writeAuditEvent } from '../lib/audit';
import { AuthErrorLayout } from '../components/auth/AuthErrorLayout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  ConversationThread,
  type ConversationThreadEntry,
} from '../components/support/ConversationThread';
// ADR-036: Beacon retired. Reply path is now an in-portal composer
// driven by post-reply Edge Function. The floating Beacon widget no
// longer renders on /support/*.
import {
  ReplyComposer,
  clearDraft,
  type ComposerErrorReason,
  type ComposerSubmitPayload,
} from '../components/support/ReplyComposer';
import { postReply, type PostReplyResult } from '../lib/post-reply-api';

interface PortalConversationSummary {
  id: number;
  number: number;
  subject: string;
  status: string;
  createdAt: string;
  /** Optional newer field from extended Edge Function. */
  modifiedAt?: string;
  threadCount: number;
  tags: { id: number; color: string; tag: string }[];
  primaryCustomer?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
  /**
   * When the Edge Function returns the detail-view envelope
   * (`get-conversation`), the sanitized portal-visible threads are
   * here at the top level. The Edge Function's `toPortalDetail` shape
   * is `{ ...summary, threads: PortalThread[] }` — NOT wrapped in
   * `_embedded` (that's the upstream HS shape, which the function
   * unwraps and sanitizes before returning to the portal).
   *
   * Issue #9 + ADR-034 (detail-view envelope mismatch fix).
   */
  threads?: ConversationThreadEntry[];
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; conversation: PortalConversationSummary }
  | { kind: 'denied' }
  | { kind: 'invalid_number' }
  | { kind: 'unauthenticated' }
  | { kind: 'error'; reason: 'network' | 'upstream' | 'unknown' };

const SUPPORT_EMAIL = 'support@solomonadvising.com';

function parseNumber(raw: string | undefined): number | null {
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0 || String(n) !== raw.trim()) return null;
  return n;
}

async function fetchConversation(
  conversationNumber: number,
  signal: AbortSignal,
): Promise<LoadState> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    return { kind: 'unauthenticated' };
  }
  // The extended Edge Function still accepts `number=<n>` for back-compat
  // and `conversationNumber=<n>` for the new contract \u2014 we send `number`
  // since that's what's already in production.
  const url = `${env.VITE_SUPABASE_URL}/functions/v1/get-conversation?number=${String(conversationNumber)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${session.access_token}` },
      signal,
    });
  } catch (err: unknown) {
    if (signal.aborted) return { kind: 'error', reason: 'network' };
    logger.error({
      source: 'conversation-detail.fetch',
      message: 'fetch threw',
      payload: { error: String(err) },
    });
    return { kind: 'error', reason: 'network' };
  }

  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status === 403) return { kind: 'denied' };
  if (res.status === 502) return { kind: 'error', reason: 'upstream' };
  if (res.status === 400) return { kind: 'invalid_number' };
  if (!res.ok) {
    logger.warn({
      source: 'conversation-detail.fetch',
      message: 'unexpected non-OK status',
      payload: { status: res.status },
    });
    return { kind: 'error', reason: 'unknown' };
  }

  try {
    const body = (await res.json()) as PortalConversationSummary;
    return { kind: 'ok', conversation: body };
  } catch (err: unknown) {
    logger.error({
      source: 'conversation-detail.fetch',
      message: 'response was not valid JSON',
      payload: { error: String(err) },
    });
    return { kind: 'error', reason: 'unknown' };
  }
}

function statusBadgeVariant(status: string): 'active' | 'closed' | 'pending' | 'outline' {
  const s = status.toLowerCase();
  if (s === 'active' || s === 'open') return 'active';
  if (s === 'closed') return 'closed';
  if (s === 'pending') return 'pending';
  return 'outline';
}

function customerName(c: PortalConversationSummary['primaryCustomer']): string {
  if (!c) return '';
  const parts = [c.firstName, c.lastName].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  );
  return parts.join(' ');
}

// (former openReplyInBeacon helper removed per ADR-036; the in-portal
// ReplyComposer now drives the reply path through the post-reply Edge
// Function.)

function mapResultToError(result: PostReplyResult): ComposerErrorReason {
  // The caller only invokes this for non-`ok` results, but TS can't
  // narrow that across function boundaries; we exhaustively switch.
  switch (result.kind) {
    case 'rejected':
      // PostReplyRejectionReason is a strict subset of
      // ComposerErrorReason (verified by reading both type defs); the
      // assignment is type-safe without an assertion.
      return result.reason;
    case 'unauthenticated':
      return 'unauthenticated';
    case 'forbidden':
      return 'forbidden';
    case 'upstream_unavailable':
      return 'upstream_unavailable';
    case 'config_error':
      return 'config_error';
    case 'network_error':
      return 'network_error';
    case 'aborted':
      // The user cancelled the request — we don't surface a visible
      // error, but the composer keeps the draft in place. Fall through
      // to 'unknown' which the copy table renders as a generic
      // "something went wrong" line, then the parent can choose to
      // suppress it (see handleSubmit).
      return 'unknown';
    case 'ok':
      // Defensive: should never be called with 'ok'. The handler
      // returns before invoking this function in the success path.
      return 'unknown';
    default: {
      const _exhaustive: never = result;
      void _exhaustive;
      return 'unknown';
    }
  }
}

function ConversationView({
  conversation,
}: {
  conversation: PortalConversationSummary;
}): ReactNode {
  const created = new Date(conversation.createdAt).toLocaleString();
  const customer = customerName(conversation.primaryCustomer);
  // The Edge Function returns sanitized threads at the top level (the
  // upstream HS `_embedded.threads` is unwrapped + sanitized by
  // `toPortalDetail` server-side). Fall back to `_embedded.threads` for
  // backward compatibility with any cached/legacy response shape —
  // belt-and-suspenders, the canonical source is `conversation.threads`.
  const threads = React.useMemo<ConversationThreadEntry[]>(
    () =>
      conversation.threads ??
      (conversation as { _embedded?: { threads?: ConversationThreadEntry[] } })._embedded
        ?.threads ??
      [],
    [conversation],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const composing = searchParams.get('compose') === '1';
  const [composerInFlight, setComposerInFlight] = React.useState(false);
  const [composerError, setComposerError] = React.useState<ComposerErrorReason | null>(null);
  const [optimisticThreads, setOptimisticThreads] = React.useState<ConversationThreadEntry[]>([]);

  const openComposer = (): void => {
    const next = new URLSearchParams(searchParams);
    next.set('compose', '1');
    setSearchParams(next, { replace: false });
    setComposerError(null);
  };

  const closeComposer = (): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('compose');
    setSearchParams(next, { replace: true });
    setComposerError(null);
  };

  const handleSubmit = async (payload: ComposerSubmitPayload): Promise<void> => {
    setComposerInFlight(true);
    setComposerError(null);
    let result: PostReplyResult;
    try {
      result = await postReply({
        conversationNumber: conversation.number,
        text: payload.text,
        attachmentIds: payload.attachmentIds,
        idempotencyKey: payload.idempotencyKey,
      });
    } finally {
      setComposerInFlight(false);
    }
    if (result.kind === 'ok') {
      // Optimistic render: append a customer thread bubble. The
      // ConversationThread renderer feeds bodies through the same
      // sanitizeThreadHtml path as the canonical render (ADR-035), so
      // any markup the user typed is safe by construction. The
      // post-reply function ALSO HTML-escapes the body on the way to
      // HS so the next read round-trip is text-only.
      const optimisticEntry: ConversationThreadEntry = {
        id: result.response.threadId ?? Date.now(),
        type: 'customer',
        createdAt: new Date().toISOString(),
        body: payload.text,
        createdBy: { type: 'customer' },
      };
      setOptimisticThreads((curr) => [...curr, optimisticEntry]);
      clearDraft({ kind: 'reply', conversationNumber: conversation.number });
      closeComposer();
      return;
    }
    setComposerError(mapResultToError(result));
  };

  const renderedThreads = React.useMemo<ConversationThreadEntry[]>(
    () => [...optimisticThreads, ...threads],
    [optimisticThreads, threads],
  );

  return (
    <article aria-labelledby="conversation-detail-title" className="flex max-w-4xl flex-col gap-6">
      <Link
        to="/support"
        className="inline-flex items-center gap-1 text-[13px] font-medium text-text-muted hover:text-orange"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        <span>Back to support</span>
      </Link>

      <header className="flex flex-col gap-3 border-b border-border-light pb-6">
        <div className="flex items-center gap-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-orange">
            Conversation #{conversation.number}
          </p>
          <Badge variant={statusBadgeVariant(conversation.status)}>{conversation.status}</Badge>
        </div>
        <h1 id="conversation-detail-title">{conversation.subject || '(No subject)'}</h1>
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-text-body">
          {customer.length > 0 ? (
            <div className="flex gap-2">
              <dt className="font-medium text-text-dark">From:</dt>
              <dd>{customer}</dd>
            </div>
          ) : null}
          <div className="flex gap-2">
            <dt className="font-medium text-text-dark">Created:</dt>
            <dd>{created}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium text-text-dark">Replies:</dt>
            <dd>{conversation.threadCount}</dd>
          </div>
        </dl>
      </header>

      <ConversationThread threads={renderedThreads} />

      {composing ? (
        <ReplyComposer
          mode={{ kind: 'reply', conversationNumber: conversation.number }}
          onCancel={closeComposer}
          onSubmit={handleSubmit}
          externalError={composerError}
          inFlight={composerInFlight}
        />
      ) : (
        <footer className="flex flex-wrap gap-3 border-t border-border-light pt-6">
          <Button type="button" onClick={openComposer}>
            <Reply className="h-4 w-4" aria-hidden />
            <span>Reply</span>
          </Button>
        </footer>
      )}
    </article>
  );
}

export function ConversationDetailRoute(): ReactNode {
  const params = useParams<{ conversationNumber?: string }>();
  const navigate = useNavigate();

  const parsed = useMemo(() => parseNumber(params.conversationNumber), [params.conversationNumber]);

  const [fetched, setFetched] = useState<{
    parsed: number | null;
    result: LoadState | null;
  }>({ parsed, result: null });

  useEffect(() => {
    if (parsed === null) return;
    const ctrl = new AbortController();
    fetchConversation(parsed, ctrl.signal)
      .then((result) => {
        if (!ctrl.signal.aborted) {
          setFetched({ parsed, result });
          if (result.kind === 'ok') {
            void writeAuditEvent({
              event_type: 'support.conversation_detail.viewed',
              payload: {
                source: 'conversation-detail.fetch',
                conversation_number: parsed,
                thread_count: result.conversation.threadCount,
              },
            });
          }
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        logger.error({
          source: 'conversation-detail.effect',
          message: 'fetchConversation rejected',
          payload: { error: String(err) },
        });
        setFetched({ parsed, result: { kind: 'error', reason: 'unknown' } });
      });
    return () => ctrl.abort();
  }, [parsed]);

  const state: LoadState = useMemo<LoadState>(() => {
    if (parsed === null) return { kind: 'invalid_number' };
    if (fetched.parsed !== parsed || fetched.result === null) {
      return { kind: 'loading' };
    }
    return fetched.result;
  }, [parsed, fetched]);

  useEffect(() => {
    if (state.kind === 'unauthenticated') {
      void navigate('/login', { replace: true });
    }
  }, [state, navigate]);

  if (state.kind === 'loading') {
    return (
      <div role="status" aria-live="polite" className="text-text-muted">
        <span className="text-[14px]">Loading conversation\u2026</span>
      </div>
    );
  }

  if (state.kind === 'ok') {
    return <ConversationView conversation={state.conversation} />;
  }

  if (state.kind === 'denied' || state.kind === 'invalid_number') {
    return (
      <AuthErrorLayout
        title="This conversation isn't available in the portal"
        description={
          <>
            If you believe this is an error, contact{' '}
            <a className="underline hover:text-orange" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>{' '}
            and we&rsquo;ll get it sorted.
          </>
        }
        primary={{ label: 'Back to support', href: '/support' }}
        secondary={{
          label: `Email ${SUPPORT_EMAIL}`,
          href: `mailto:${SUPPORT_EMAIL}`,
        }}
      />
    );
  }

  if (state.kind === 'unauthenticated') {
    return (
      <div role="status" aria-live="polite" className="text-text-muted">
        Redirecting to sign-in\u2026
      </div>
    );
  }

  const errorCopy =
    state.reason === 'upstream'
      ? 'Our support backend is briefly unavailable. Please try again in a moment.'
      : 'Something went wrong loading this conversation. Please try again.';
  return (
    <AuthErrorLayout
      title="We couldn't load this conversation"
      description={errorCopy}
      primary={{
        label: 'Try again',
        onClick: () => {
          void navigate(0);
        },
      }}
      secondary={{ label: 'Back to support', href: '/support' }}
      tertiary={{
        label: `Email ${SUPPORT_EMAIL}`,
        href: `mailto:${SUPPORT_EMAIL}`,
      }}
    />
  );
}
